// Generic appointment-booking demo template.
// Covers 13 industries via BOOKING_CONFIGS.
// Shows the partner what the "X Demo" looks like: service list + time slot
// picker + customer form → success message. Deliberately plain.

import { useState } from 'react'
import DemoLayout from './shared/DemoLayout'
import type { ChatPersona } from './shared/ChatWidget'
import { useT } from './shared/i18n-ctx'
import type { Dict } from './shared/i18n-ctx'
import { BOOKING_CONFIGS } from './configs'

const dict: Dict = {
  demo_tag: { en: 'Demo · Appointment booking', mk: 'Демо · Резервација на термин', gr: 'Demo · Κράτηση ραντεβού' },
  book: { en: 'Book now', mk: 'Резервирај', gr: 'Κλείσε τώρα' },
  service: { en: 'Service', mk: 'Услуга', gr: 'Υπηρεσία' },
  select_service: { en: 'Select a service', mk: 'Избери услуга', gr: 'Επιλέξτε υπηρεσία' },
  date: { en: 'Date', mk: 'Датум', gr: 'Ημερομηνία' },
  time: { en: 'Time', mk: 'Време', gr: 'Ώρα' },
  name: { en: 'Your name', mk: 'Твое име', gr: 'Όνομα' },
  phone: { en: 'Phone', mk: 'Телефон', gr: 'Τηλέφωνο' },
  confirm: { en: 'Confirm booking', mk: 'Потврди', gr: 'Επιβεβαίωση' },
  submitted: { en: '✓ Booked. You will get an SMS confirmation.', mk: '✓ Резервирано. Ќе добиете СМС.', gr: '✓ Κράτηση ολοκληρώθηκε. SMS επιβεβαίωση.' },
  features: { en: 'What\'s included', mk: 'Што вклучува', gr: 'Τι περιλαμβάνεται' },
  f1: { en: 'Online booking 24/7', mk: 'Онлајн резервација 24/7', gr: 'Κρατήσεις 24/7' },
  f2: { en: 'SMS reminder 24h before', mk: 'СМС потсетник 24ч пред', gr: 'SMS 24 ώρες πριν' },
  f3: { en: 'Staff schedule dashboard', mk: 'Распоред на персонал', gr: 'Πρόγραμμα προσωπικού' },
  f4: { en: 'Auto-rebook for regulars', mk: 'Автоматско повторно резервирање', gr: 'Αυτόματη επανάληψη' },
  f5: { en: 'Multi-language (EN/MK/GR)', mk: 'Повеќе јазици (EN/MK/GR)', gr: 'Πολύγλωσσο (EN/MK/GR)' },
  f6: { en: 'Works on any phone', mk: 'Работи на било кој телефон', gr: 'Σε οποιοδήποτε τηλέφωνο' },
  services_header: { en: 'Available services', mk: 'Достапни услуги', gr: 'Διαθέσιμες υπηρεσίες' },
  min: { en: 'min', mk: 'мин', gr: 'λεπτά' },
}

const TIMES = ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00']

export default function BookingDemo({ slug }: { slug: string }) {
  const config = BOOKING_CONFIGS[slug]
  if (!config) return <NotConfigured slug={slug} />
  const persona: ChatPersona = {
    name: config.industry.en,
    kind: 'booking',
    items: config.services.map((s) => ({ name: s.name, price: s.price })),
  }
  return (
    <DemoLayout dict={dict} accent="from-indigo-500 to-purple-600" chatPersona={persona}>
      <Body slug={slug} />
    </DemoLayout>
  )
}

function NotConfigured({ slug }: { slug: string }) {
  return (
    <div className="max-w-xl mx-auto p-10 text-center">
      <p className="text-ink-100">No booking config for &quot;{slug}&quot;.</p>
    </div>
  )
}

function Body({ slug }: { slug: string }) {
  const { t, lang } = useT()
  const config = BOOKING_CONFIGS[slug]
  const [serviceIdx, setServiceIdx] = useState<number | null>(null)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <div className="bg-gradient-to-b from-ink-950 via-ink-950 to-ink-900">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <span className="chip bg-brand-500/10 text-brand-500 border border-brand-500/20 mb-5">
          {config.icon} {t('demo_tag')}
        </span>
        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white">
          Demo · {config.industry[lang]}
        </h1>
        <p className="mt-4 text-lg text-ink-100 max-w-2xl">{config.tagline[lang]}</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 lg:grid-cols-[2fr_3fr] pb-12">
        {/* Services list */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">{t('services_header')}</h2>
          <ul className="space-y-2">
            {config.services.map((s, i) => (
              <li key={i}>
                <button
                  onClick={() => {
                    setSubmitted(false)
                    setServiceIdx(i)
                  }}
                  className={`w-full flex items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors ${
                    serviceIdx === i
                      ? 'bg-brand-600/20 border-brand-500/50'
                      : 'bg-ink-900 border-ink-800 hover:border-ink-500'
                  }`}
                >
                  <div>
                    <div className="text-white font-medium">{s.name}</div>
                    <div className="text-xs text-ink-500">
                      {s.duration === '—' ? '—' : `${s.duration}`}
                    </div>
                  </div>
                  <div className="text-emerald-400 font-semibold whitespace-nowrap">
                    {s.price === 0 ? 'Free' : `€${s.price}`}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Booking form */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">{t('book')}</h2>
          {submitted ? (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-4 text-center font-medium">
              {t('submitted')}
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <span className="label">{t('service')}</span>
                <div className="input bg-ink-900 text-ink-100">
                  {serviceIdx !== null ? config.services[serviceIdx].name : t('select_service')}
                </div>
              </div>
              <label>
                <span className="label">{t('date')}</span>
                <input required type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <label>
                <span className="label">{t('time')}</span>
                <select required className="input" value={time} onChange={(e) => setTime(e.target.value)}>
                  <option value="">—</option>
                  {TIMES.map((t2) => (
                    <option key={t2} value={t2}>{t2}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="label">{t('name')}</span>
                <input required className="input" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label>
                <span className="label">{t('phone')}</span>
                <input required type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
              <button
                type="submit"
                className="btn-primary sm:col-span-2"
                disabled={serviceIdx === null}
              >
                {serviceIdx === null ? t('select_service') : t('confirm')}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-2xl font-bold text-white mb-6">{t('features')}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {['f1', 'f2', 'f3', 'f4', 'f5', 'f6'].map((k) => (
            <div key={k} className="card flex gap-3 items-start">
              <span className="text-2xl">✓</span>
              <span className="text-ink-100">{t(k)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
